import { Box, Typography, IconButton, Chip, CircularProgress } from '@mui/material';
import { ContentCopy as CopyIcon, Edit as EditIcon, Cancel as CancelIcon, CheckCircle as ActivateIcon } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import type { LicenseKey } from '../types';
import { formatApiDateTime, isDateExpired } from '../utils/license-utils';

interface LicenseTableProps {
  licenses: LicenseKey[];
  loading: boolean;
  selectionModel: GridRowSelectionModel;
  onSelectionChange: (model: GridRowSelectionModel) => void;
  onEdit: (license: LicenseKey) => void;
  onToggleActivation: (id: number, current: boolean) => void;
  onCopyKey: (key: string) => void;
}

/**
 * 许可证列表表格组件（纯展示）
 */
export default function LicenseTable({
  licenses,
  loading,
  selectionModel,
  onSelectionChange,
  onEdit,
  onToggleActivation,
  onCopyKey,
}: LicenseTableProps) {
  const { t } = useTranslation();

  const columns: GridColDef<LicenseKey>[] = [
    { field: 'id', headerName: t('licenses.id'), width: 70 },
    {
      field: 'key',
      headerName: t('licenses.key'),
      width: 220,
      renderCell: (params: GridRenderCellParams<LicenseKey>) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontFamily="monospace">
            {params.value}
          </Typography>
          <IconButton size="small" onClick={() => onCopyKey(params.value)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'activations',
      headerName: t('licenses.activationsColumn'),
      width: 130,
      renderCell: (params: GridRenderCellParams<LicenseKey>) => {
        const current = params.row?.activation_count ?? 0;
        const max = params.row?.max_activations ?? 0;
        return `${current} / ${max}`;
      },
    },
    {
      field: 'created_at',
      headerName: t('licenses.createdAt'),
      width: 190,
      renderCell: (params: GridRenderCellParams<LicenseKey>) => formatApiDateTime(params.row?.created_at, t('licenses.invalidDate')),
    },
    {
      field: 'expires_at',
      headerName: t('licenses.expiresAt'),
      width: 190,
      renderCell: (params: GridRenderCellParams<LicenseKey>) => {
        const value = params.row?.expires_at;
        if (!value) {
          return t('licenses.never');
        }

        const formatted = formatApiDateTime(value, t('licenses.invalidDate'));
        if (formatted === t('licenses.invalidDate')) {
          return formatted;
        }

        return isDateExpired(value) ? t('licenses.expired') : formatted;
      },
    },
    {
      field: 'is_active',
      headerName: t('licenses.status'),
      width: 110,
      renderCell: (params: GridRenderCellParams<LicenseKey>) => (
        <Chip
          label={params.value ? t('licenses.active') : t('licenses.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'notes', headerName: t('licenses.notesColumn'), width: 160, flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('licenses.actions'),
      width: 130,
      getActions: (params: GridRowParams<LicenseKey>) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label={t('common.edit')}
          onClick={() => onEdit(params.row)}
        />,
        <GridActionsCellItem
          key="toggle"
          icon={params.row.is_active ? <CancelIcon /> : <ActivateIcon />}
          label={params.row.is_active ? t('licenses.deactivate') : t('licenses.activate')}
          onClick={() => onToggleActivation(params.row.id, params.row.is_active)}
          showInMenu={false}
        />,
      ],
    },
  ];

  return (
    <Paper>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={licenses}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onSelectionChange}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
        />
      )}
    </Paper>
  );
}
